package co.maytes.standupbot.dashboard.features.home

import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.billing.BillingStatusDto
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UnlinkedPeopleDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import kotlinx.coroutines.CoroutineScope

class HomeContainer(
    scope: CoroutineScope,
    private val api: ApiClient,
) : Container<HomeContract.State, HomeContract.Intent, HomeContract.Action> {

    override val store = scope.store<
            HomeContract.State,
            HomeContract.Intent,
            HomeContract.Action,
            >(initialState = HomeContract.State()) {
        configure(name = "Home")
        logging()
        reduce { intent ->
            when (intent) {
                is HomeContract.Intent.Load -> {
                    updateState { copy(workspaceId = intent.workspaceId, canManage = intent.canManage) }
                    load()
                }
                is HomeContract.Intent.ViewRun ->
                    action(HomeContract.Action.Navigate("/app/runs/${intent.runId}"))
                HomeContract.Intent.ViewAllRuns ->
                    action(HomeContract.Action.Navigate("/app/runs"))
                HomeContract.Intent.ManageStandups ->
                    action(HomeContract.Action.Navigate("/app/standups"))
            }
        }
    }

    private suspend fun StoreScope<HomeContract.State, HomeContract.Action>.load() {
        val ws = currentState.workspaceId
        if (ws.isBlank()) return

        updateState { copy(status = HomeContract.Status.Loading) }

        val runsRes = api.get<PagedResponse<StandupRunSummaryDto>>("/api/v1/workspaces/$ws/runs")
        val standupsRes = api.get<List<StandupDto>>("/api/v1/workspaces/$ws/standups")
        val billingRes = api.get<BillingStatusDto>("/api/v1/workspaces/$ws/billing")
        val membersRes = api.get<List<WorkspaceMemberDto>>("/api/v1/workspaces/$ws/members")
        val unlinkedRes =
            if (currentState.canManage) api.get<UnlinkedPeopleDto>("/api/v1/workspaces/$ws/people/unlinked") else null

        when {
            runsRes is Result.Err -> updateState { copy(status = HomeContract.Status.Error(runsRes.error)) }
            standupsRes is Result.Err -> updateState { copy(status = HomeContract.Status.Error(standupsRes.error)) }
            runsRes is Result.Ok && standupsRes is Result.Ok ->
                updateState {
                    copy(
                        runs = runsRes.value.items,
                        standups = standupsRes.value,
                        billingStatus = (billingRes as? Result.Ok)?.value,
                        members = (membersRes as? Result.Ok)?.value,
                        unlinked = (unlinkedRes as? Result.Ok)?.value,
                        status = HomeContract.Status.Ready,
                    )
                }
        }
    }
}
